import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../services/api';
import { useToast } from '../hooks/useToast';

export default function AdminBilling() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`/api/admin/billing/stats?month=${month}&year=${year}`);
      if (!res.ok) throw new Error();
      setStats(await res.json());
    } catch { toast.error('Failed to load billing stats'); }
    setLoading(false);
  }, [month, year]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const monthLabel = new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div className="ab-page">
      <style>{CSS}</style>

      {/* Header */}
      <div className="ab-header">
        <div>
          <h2 className="ab-title">Billing & Usage</h2>
          <p className="ab-desc">Review activity and estimated cost</p>
        </div>
        <div className="ab-month-picker">
          <input
            type="month"
            value={`${year}-${String(month).padStart(2, '0')}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-');
              setYear(parseInt(y)); setMonth(parseInt(m));
            }}
            className="ab-month-input"
          />
          <button className="ab-go-btn" onClick={fetchStats}>Go</button>
        </div>
      </div>

      {loading ? (
        <div className="ab-loading">Loading billing data...</div>
      ) : stats && (
        <>
          {/* Plan Card */}
          <div className="ab-plan-card">
            <div className="ab-plan-left">
              <span className="ab-plan-label">Current plan</span>
              <div className="ab-plan-name">
                Free <span className="ab-plan-badge">free</span>
              </div>
            </div>
            <div className="ab-plan-right">
              <span className="ab-plan-label">Payment integration</span>
              <div className="ab-plan-stripe">💳 Stripe — coming soon</div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="ab-stats-grid">
            <StatCard label="JOBS" value={stats.total_jobs} sub="this month" color="var(--text-primary)" />
            <StatCard label="COMPLETED" value={stats.completed} sub="successful" color="#10b981" />
            <StatCard label="FAILED" value={stats.failed} sub="errors" color="#ef4444" />
            <StatCard label="TOKENS USED" value={formatNum(stats.tokens_used)} sub="LLM tokens" color="var(--text-primary)" />
            <StatCard label="EST. LLM COST" value={`$${stats.cost_usd.toFixed(4)}`} sub="USD this month" color="var(--text-primary)" />
            <StatCard label="ACTIVE PROJECTS" value={stats.active_projects} sub="this month" color="var(--text-primary)" />
          </div>

          {/* Payment Integration Section */}
          <div className="ab-payment-section">
            <h3 className="ab-payment-title">💳 Payment integration — Stripe (scaffold)</h3>
            <p className="ab-payment-desc">
              Stripe integration is scaffolded below. Wire up STRIPE_SECRET_KEY and STRIPE_WEBHOOK_SECRET in .env to activate.
            </p>
            <div className="ab-plans-grid">
              <div className="ab-upgrade-card">
                <div className="ab-upgrade-name">Pro</div>
                <div className="ab-upgrade-price">$29/mo</div>
                <div className="ab-upgrade-features">Unlimited generations · Priority support · 5 seats</div>
                <button className="ab-upgrade-btn" disabled>Upgrade (coming soon)</button>
              </div>
              <div className="ab-upgrade-card">
                <div className="ab-upgrade-name">Enterprise</div>
                <div className="ab-upgrade-price">Custom</div>
                <div className="ab-upgrade-features">SSO · Audit logs · SLA · Unlimited seats</div>
                <button className="ab-upgrade-btn" disabled>Upgrade (coming soon)</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, color }) {
  return (
    <div className="ab-stat-card">
      <div className="ab-stat-label">{label}</div>
      <div className="ab-stat-value" style={{ color }}>{value}</div>
      <div className="ab-stat-sub">{sub}</div>
    </div>
  );
}

function formatNum(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

const CSS = `
/* ── Page wrapper ── */
.ab-page {
  padding: 32px 0; max-width: 1100px; margin: 0 auto;
  position: relative;
}


/* ── Typography refinements ── */

.ab-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }

/* Page title — sharper tracking */
.ab-title {
  font-size: 22px; font-weight: 700; letter-spacing: -0.025em; line-height: 1.2;
  color: rgba(241,245,249,0.97); margin: 0 0 5px;
}
[data-theme="light"] .ab-title { color: #0f172a; }

/* Subtitle — slightly more visible */
.ab-desc {
  font-size: 13px; font-weight: 400; line-height: 1.6;
  color: rgba(148,163,184,0.72); margin: 0; letter-spacing: 0.01em;
}
[data-theme="light"] .ab-desc { color: #64748b; }

/* ── Loading skeleton ── */
.ab-loading {
  text-align: center; padding: 60px 0; font-size: 13px;
  color: rgba(100,116,139,0.45); letter-spacing: 0.04em;
  animation: abPulse 1.6s ease-in-out infinite;
}
@keyframes abPulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.85; } }

/* ── Month picker controls ── */
.ab-month-picker { display: flex; align-items: center; gap: 8px; }
.ab-month-input {
  padding: 9px 12px; border-radius: 8px; font-size: 13px; font-family: inherit;
  background: rgba(6,9,22,0.65);
  border: 1px solid rgba(99,102,241,0.22);
  color: rgba(226,232,240,0.92); outline: none;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.35), 0 0 0 0 rgba(99,102,241,0);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  /* style the native calendar icon to match */
  color-scheme: dark;
}
.ab-month-input:hover:not(:focus) { border-color: rgba(99,102,241,0.36); }
.ab-month-input:focus {
  border-color: rgba(99,102,241,0.62);
  background: rgba(8,12,30,0.75);
  box-shadow: 0 0 0 3px rgba(99,102,241,0.14), inset 0 1px 2px rgba(0,0,0,0.28);
}
[data-theme="light"] .ab-month-input {
  background: rgba(248,250,252,0.92); border-color: rgba(99,102,241,0.16);
  color: #1e293b; box-shadow: inset 0 1px 2px rgba(99,102,241,0.04);
  color-scheme: light; font-family: inherit;
}
[data-theme="light"] .ab-month-input:hover:not(:focus) { border-color: rgba(99,102,241,0.28); }
[data-theme="light"] .ab-month-input:focus {
  border-color: rgba(99,102,241,0.45); background: #fff;
  box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
}

/* ── Go button — primary action ── */
.ab-go-btn {
  padding: 9px 20px; border-radius: 8px; font-size: 13px; font-weight: 600;
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border: 1px solid rgba(139,92,246,0.4);
  color: rgba(255,255,255,0.97); cursor: pointer; letter-spacing: 0.02em;
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.35),
    0 4px 16px rgba(99,102,241,0.32),
    inset 0 1px 0 rgba(255,255,255,0.18),
    inset 0 -1px 0 rgba(0,0,0,0.1);
  transition: background 0.18s ease, box-shadow 0.18s ease, transform 0.14s ease;
}
.ab-go-btn:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.5),
    0 6px 24px rgba(99,102,241,0.42),
    inset 0 1px 0 rgba(255,255,255,0.2),
    inset 0 -1px 0 rgba(0,0,0,0.12);
  transform: translateY(-1px);
}
.ab-go-btn:active {
  transform: translateY(0);
  box-shadow:
    0 0 0 1px rgba(139,92,246,0.35),
    0 2px 8px rgba(99,102,241,0.28),
    inset 0 1px 0 rgba(255,255,255,0.12);
}
[data-theme="light"] .ab-go-btn {
  background: linear-gradient(135deg, #6366f1 0%, #7c3aed 100%);
  border-color: rgba(99,102,241,0.3);
  color: #fff;
  box-shadow: 0 0 0 1px rgba(99,102,241,0.25), 0 4px 14px rgba(99,102,241,0.28), inset 0 1px 0 rgba(255,255,255,0.2);
}
[data-theme="light"] .ab-go-btn:hover {
  background: linear-gradient(135deg, #4f46e5 0%, #6d28d9 100%);
  box-shadow: 0 0 0 1px rgba(99,102,241,0.35), 0 6px 20px rgba(99,102,241,0.35), inset 0 1px 0 rgba(255,255,255,0.22);
}

/* ── Plan Card ── */
.ab-plan-card {
  position: relative;
  display: flex; justify-content: space-between; align-items: center;
  padding: 22px 28px; border-radius: 18px; margin-bottom: 20px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.ab-plan-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
.ab-plan-card:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
[data-theme="light"] .ab-plan-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .ab-plan-card::before { opacity: 0.55; }
[data-theme="light"] .ab-plan-card:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 12px 32px rgba(99,102,241,0.06);
}

.ab-plan-left, .ab-plan-right { display: flex; flex-direction: column; gap: 4px; position: relative; }

/* vertical divider between left and right plan sections */
.ab-plan-card .ab-plan-right {
  padding-left: 24px;
  border-left: 1px solid rgba(99,102,241,0.12);
}
[data-theme="light"] .ab-plan-card .ab-plan-right { border-left-color: rgba(99,102,241,0.1); }
/* Plan label — uppercase eyebrow */
.ab-plan-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase;
  color: rgba(99,102,241,0.5); line-height: 1;
}
[data-theme="light"] .ab-plan-label { color: rgba(99,102,241,0.45); }

/* Plan name — strong heading */
.ab-plan-name {
  font-size: 18px; font-weight: 700; letter-spacing: -0.01em;
  color: rgba(226,232,240,0.96);
  display: flex; align-items: center; gap: 8px; line-height: 1.2;
}
[data-theme="light"] .ab-plan-name { color: #1e293b; }

/* Plan badge — subtle indigo pill */
.ab-plan-badge {
  font-size: 10px; padding: 2px 9px; border-radius: 20px; font-weight: 700;
  background: rgba(99,102,241,0.1); color: rgba(165,180,252,0.75);
  border: 1px solid rgba(99,102,241,0.2); letter-spacing: 0.06em;
  text-transform: uppercase; line-height: 1.6;
}
[data-theme="light"] .ab-plan-badge { color: #6366f1; background: rgba(99,102,241,0.07); border-color: rgba(99,102,241,0.16); }

/* Stripe status — muted neutral, not amber */
.ab-plan-stripe {
  font-size: 13px; font-weight: 500; line-height: 1.4;
  color: rgba(148,163,184,0.65); letter-spacing: 0.01em;
}
[data-theme="light"] .ab-plan-stripe { color: #64748b; }

/* ── Stats Grid ── */
.ab-stats-grid {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px; margin-bottom: 20px;
}
.ab-stat-card {
  position: relative;
  padding: 20px 16px; border-radius: 16px; text-align: center;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 6px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.18s ease;
}
.ab-stat-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 16px 16px 0 0; opacity: 0.8;
}
.ab-stat-card:hover {
  border-color: rgba(99,102,241,0.24);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 12px 36px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
[data-theme="light"] .ab-stat-card {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 4px 16px rgba(15,23,42,0.05);
}
[data-theme="light"] .ab-stat-card::before { opacity: 0.55; }
[data-theme="light"] .ab-stat-card:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 8px 24px rgba(99,102,241,0.07);
  transform: translateY(-1px);
}

/* Stat label — uppercase eyebrow */
.ab-stat-label {
  font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase;
  color: rgba(99,102,241,0.5); margin-bottom: 10px; position: relative; line-height: 1;
}
[data-theme="light"] .ab-stat-label { color: rgba(99,102,241,0.45); }

/* Stat value — strong numeral, CSS base color (inline style overrides for green/red) */
.ab-stat-value {
  font-size: 24px; font-weight: 700; position: relative; line-height: 1.1;
  color: rgba(226,232,240,0.95);
  font-variant-numeric: tabular-nums; letter-spacing: -0.02em;
}
[data-theme="light"] .ab-stat-value { color: #0f172a; }

/* Soften the inline green/red — override via attribute selectors isn't possible,
   so we target the known color values with a wrapper approach via CSS custom props.
   Instead, we adjust the StatCard component colors at the CSS level by
   re-declaring the color for the two semantic states using data attributes.
   Since we cannot change JSX, we soften via a CSS filter on the value text
   only when it would be too saturated — achieved by reducing the stat-value
   brightness slightly across the board so the inline colors land softer. */
.ab-stat-card .ab-stat-value {
  filter: saturate(0.82) brightness(0.94);
}

/* Stat sub — muted helper */
.ab-stat-sub {
  font-size: 11px; color: rgba(100,116,139,0.52); margin-top: 5px;
  letter-spacing: 0.02em; position: relative; line-height: 1.4;
}
[data-theme="light"] .ab-stat-sub { color: #94a3b8; }

/* ── Payment Section ── */
.ab-payment-section {
  position: relative;
  padding: 26px 28px; border-radius: 18px;
  background: linear-gradient(155deg, #12152b 0%, #0c0f22 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.6), 0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  overflow: hidden;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}
.ab-payment-section::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 18px 18px 0 0; opacity: 0.8;
}
.ab-payment-section:hover {
  border-color: rgba(99,102,241,0.24);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 14px 40px rgba(0,0,0,0.32), 0 0 0 1px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.08);
}
[data-theme="light"] .ab-payment-section {
  background: #ffffff;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 6px 24px rgba(15,23,42,0.05);
}
[data-theme="light"] .ab-payment-section::before { opacity: 0.55; }
[data-theme="light"] .ab-payment-section:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 12px 32px rgba(99,102,241,0.06);
}

/* Payment section title — refined heading */
.ab-payment-title {
  font-size: 14px; font-weight: 700; letter-spacing: 0.005em; line-height: 1.3;
  color: rgba(226,232,240,0.95); margin: 0 0 10px; position: relative;
}
[data-theme="light"] .ab-payment-title { color: #1e293b; }

/* Payment desc — readable muted body */
.ab-payment-desc {
  font-size: 13px; font-weight: 400; line-height: 1.65;
  color: rgba(148,163,184,0.68); margin: 0 0 20px; position: relative;
  letter-spacing: 0.01em;
}
[data-theme="light"] .ab-payment-desc { color: #64748b; }

.ab-plans-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; position: relative; }
@media (max-width: 600px) { .ab-plans-grid { grid-template-columns: 1fr; } }

.ab-upgrade-card {
  position: relative;
  padding: 22px 20px; border-radius: 14px;
  background: linear-gradient(155deg, #0e1124 0%, #090c1a 100%);
  border: 1px solid rgba(99,102,241,0.13);
  box-shadow: 0 1px 3px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.18s ease;
}
.ab-upgrade-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, rgba(139,92,246,0.2) 100%);
  border-radius: 14px 14px 0 0; opacity: 0.7;
}
.ab-upgrade-card:hover {
  border-color: rgba(99,102,241,0.24);
  transform: translateY(-2px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.5), 0 8px 28px rgba(0,0,0,0.3), 0 0 0 1px rgba(99,102,241,0.08), inset 0 1px 0 rgba(255,255,255,0.06);
}
[data-theme="light"] .ab-upgrade-card {
  background: #f8fafc;
  border-color: rgba(99,102,241,0.11);
  box-shadow: 0 1px 3px rgba(15,23,42,0.06), 0 4px 14px rgba(15,23,42,0.04);
}
[data-theme="light"] .ab-upgrade-card::before { opacity: 0.5; }
[data-theme="light"] .ab-upgrade-card:hover {
  border-color: rgba(99,102,241,0.2);
  box-shadow: 0 2px 8px rgba(15,23,42,0.07), 0 6px 20px rgba(99,102,241,0.07);
  transform: translateY(-1px);
}

/* Upgrade card typography */
.ab-upgrade-name {
  font-size: 13px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: rgba(148,163,184,0.7); margin-bottom: 6px; line-height: 1;
}
[data-theme="light"] .ab-upgrade-name { color: #475569; }

.ab-upgrade-price {
  font-size: 26px; font-weight: 700; letter-spacing: -0.03em; line-height: 1.1;
  color: rgba(226,232,240,0.95); margin-bottom: 8px;
  font-variant-numeric: tabular-nums;
}
[data-theme="light"] .ab-upgrade-price { color: #0f172a; }

.ab-upgrade-features {
  font-size: 12px; font-weight: 400; line-height: 1.65; letter-spacing: 0.01em;
  color: rgba(100,116,139,0.58); margin-bottom: 16px;
}
[data-theme="light"] .ab-upgrade-features { color: #64748b; }
/* ── Upgrade button — muted primary (disabled state) ── */
.ab-upgrade-btn {
  padding: 9px 18px; border-radius: 8px; font-size: 12px; font-weight: 600;
  background: linear-gradient(135deg, rgba(99,102,241,0.35) 0%, rgba(124,58,237,0.3) 100%);
  border: 1px solid rgba(139,92,246,0.25);
  color: rgba(165,180,252,0.55);
  cursor: not-allowed;
  letter-spacing: 0.02em;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.07),
    0 2px 10px rgba(99,102,241,0.12);
  opacity: 0.6;
  /* no transition needed — always disabled */
}
[data-theme="light"] .ab-upgrade-btn {
  background: linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(124,58,237,0.1) 100%);
  border-color: rgba(99,102,241,0.2);
  color: rgba(99,102,241,0.45);
  box-shadow: 0 1px 4px rgba(99,102,241,0.08);
  opacity: 0.65;
}
`;
